import { NavLink } from 'react-router-dom';
import { APP_NAME, APP_SHORT, DESCRIPTOR } from '@strata/config';
import { Logo } from './Logo.js';
import { NAV_ITEMS, type NavItem } from './nav.js';

export interface SidebarProps {
  items?: readonly NavItem[];
  /** Mobile drawer open state. Ignored ≥lg, where the sidebar is always docked. */
  open?: boolean;
  /** Close the mobile drawer (tapping a link or the backdrop). No-op on desktop. */
  onClose?: () => void;
}

/**
 * Navy sidebar (CLAUDE.md §2 layout). Docked at 230px from `lg` up; below that it becomes an
 * off-canvas drawer (the app is responsive + PWA per §11) toggled by the TopBar hamburger, sliding
 * over a dimmed backdrop. Active row gets the `ink-3` surface via <NavLink>.
 */
export function Sidebar({ items = NAV_ITEMS, open = false, onClose }: SidebarProps) {
  return (
    <>
      {/* Backdrop — only present on mobile while the drawer is open. */}
      {open ? (
        <button
          type="button"
          aria-label="Close menu"
          onClick={onClose}
          className="fixed inset-0 z-20 bg-ink/50 lg:hidden"
        />
      ) : null}

      <aside
        className={[
          'fixed inset-y-0 left-0 z-30 flex w-sidebar flex-col bg-ink-2 text-ink-text',
          'transition-transform duration-200 ease-out lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        aria-label="Primary"
        aria-hidden={open ? undefined : false}
      >
        <div className="flex h-topbar items-center px-5">
          <Logo on_dark />
        </div>
        <div className="px-5 pb-3 text-xs font-medium text-ink-text/60">{DESCRIPTOR}</div>

        <nav className="mt-2 flex-1 space-y-1 overflow-y-auto px-3" aria-label={`${APP_NAME} sections`}>
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end ?? false}
              onClick={onClose}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-ink-3 text-white'
                    : 'text-ink-text/80 hover:bg-ink-3/50 hover:text-white',
                ].join(' ')
              }
            >
              <Icon size={18} strokeWidth={2} aria-hidden />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-5 py-4 text-[11px] text-ink-text/40">{APP_SHORT} · Atheon AI</div>
      </aside>
    </>
  );
}
